import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import { Download as DownloadIcon, QrCode2 as QrIcon, Print as PrintIcon } from '@mui/icons-material';
import { generateVehicleQR } from '../utils/qrGenerator';

const QRCodeGenerator = () => {
  const [vehicleNumber, setVehicleNumber] = useState('AP39KU2747');
  const [flatNumber, setFlatNumber] = useState('A-101');
  const [generatedQR, setGeneratedQR] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Generate QR code for specific vehicle
  const handleGenerateQR = async () => {
    if (!vehicleNumber.trim()) {
      setError('Please enter a vehicle number');
      return;
    }

    if (!flatNumber.trim()) {
      setError('Please enter a flat number');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const qr = await generateVehicleQR(vehicleNumber, flatNumber);
      setGeneratedQR({
        vehicleNumber: vehicleNumber.trim().toUpperCase(),
        ...qr
      });
    } catch (err) {
      setError('Failed to generate QR code: ' + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download QR code
  const downloadQR = (qr) => {
    const link = document.createElement('a');
    link.href = qr.dataUrl;
    link.download = `QR_${qr.vehicleNumber}_${qr.flatNumber}.png`;
    link.click();
  };

  // Print QR code
  const printQR = (qr) => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${qr.vehicleNumber}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              text-align: center;
            }
            .qr-container {
              max-width: 400px;
              margin: 0 auto;
              padding: 20px;
              border: 2px solid #ddd;
              border-radius: 8px;
            }
            .qr-code {
              width: 250px;
              height: 250px;
              margin: 20px auto;
            }
            .vehicle-info {
              margin: 20px 0;
            }
            .vehicle-number {
              font-size: 24px;
              font-weight: bold;
              margin: 10px 0;
            }
            .flat-id {
              font-size: 18px;
              color: #666;
              margin: 10px 0;
            }
            .qr-data {
              font-size: 12px;
              color: #999;
              word-break: break-all;
              margin-top: 15px;
            }
            @media print {
              body { margin: 0; }
              .qr-container { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <h2>Vehicle QR Code</h2>
            <img src="${qr.dataUrl}" alt="QR Code for ${qr.vehicleNumber}" class="qr-code" />
            <div class="vehicle-info">
              <div class="vehicle-number">Vehicle: ${qr.vehicleNumber}</div>
              <div class="flat-id">Flat: ${qr.flatNumber}</div>
              <div class="qr-data">QR Data: ${qr.data}</div>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Print multiple QR codes
  const printAllQRs = (qrCodes) => {
    const printWindow = window.open('', '_blank');
    const qrCardsHtml = qrCodes.map(qr => `
      <div class="qr-card">
        <h3>Vehicle QR Code</h3>
        <img src="${qr.dataUrl}" alt="QR Code for ${qr.vehicleNumber}" class="qr-code" />
        <div class="vehicle-info">
          <div class="vehicle-number">Vehicle: ${qr.vehicleNumber}</div>
          <div class="flat-id">Flat: ${qr.flatNumber}</div>
          <div class="qr-data">QR Data: ${qr.data}</div>
        </div>
      </div>
    `).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Vehicle QR Codes</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
            }
            .qr-card {
              max-width: 350px;
              margin: 20px auto;
              padding: 20px;
              border: 2px solid #ddd;
              border-radius: 8px;
              text-align: center;
              page-break-after: auto;
              display: inline-block;
              vertical-align: top;
            }
            .qr-code {
              width: 200px;
              height: 200px;
              margin: 15px auto;
            }
            .vehicle-number {
              font-size: 20px;
              font-weight: bold;
              margin: 10px 0;
            }
            .flat-id {
              font-size: 16px;
              color: #666;
              margin: 10px 0;
            }
            .qr-data {
              font-size: 10px;
              color: #999;
              word-break: break-all;
              margin-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .qr-card { 
                border: 1px solid #ddd; 
                margin: 10px;
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <h1 style="text-align: center; margin-bottom: 30px;">Vehicle QR Codes</h1>
          ${qrCardsHtml}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Generate initial QR code on component mount
  useEffect(() => {
    handleGenerateQR();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom align="center" color="primary">
        Vehicle QR Code Generator
      </Typography>

      <Grid container spacing={3}>
        {/* QR Generator Form */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Generate Vehicle QR Code
            </Typography>

            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Vehicle Number"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                placeholder="AP39KU2747"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                required
                label="Flat Number"
                value={flatNumber}
                onChange={(e) => setFlatNumber(e.target.value.toUpperCase())}
                placeholder="A-101"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<QrIcon />}
                onClick={handleGenerateQR}
                disabled={isGenerating}
                fullWidth
              >
                Generate QR Code
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Generated QR Display */}
        <Grid item xs={12} md={6}>
          {generatedQR && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Generated QR Code
              </Typography>

              <Card sx={{ mb: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <img
                    src={generatedQR.dataUrl}
                    alt={`QR Code for ${generatedQR.vehicleNumber}`}
                    style={{ maxWidth: '200px', width: '100%' }}
                  />
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Vehicle: {generatedQR.vehicleNumber}
                  </Typography>
                  <Typography variant="subtitle2">
                    Flat: {generatedQR.flatNumber}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1, wordBreak: 'break-all' }}>
                    Data: {generatedQR.data}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadQR(generatedQR)}
                      variant="outlined"
                      size="small"
                    >
                      Download
                    </Button>
                    <Button
                      startIcon={<PrintIcon />}
                      onClick={() => printQR(generatedQR)}
                      variant="contained"
                      size="small"
                    >
                      Print
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default QRCodeGenerator;
